<template>
  <div class="field">
    <!-- <label class="label">{{ $t("selfie") }} *</label> -->

    <div v-if="!isCameraActive && !selfiePreview" class="buttons">
      <button type="button" class="button is-primary" @click="activateCamera">
        <span class="icon"><i class="fa fa-camera"></i></span>
        <span>{{ $t("take selfie") }}</span>
      </button>

      <div class="file">
        <label class="file-label">
          <input
            class="file-input"
            type="file"
            accept="image/*"
            @change="$emit('handle-file-upload', 'selfie', $event)"
          />
          <span class="file-cta">
            <span class="file-icon">
              <i class="fa fa-upload"></i>
            </span>
            <span class="file-label">{{ $t("or upload file") }}</span>
          </span>
        </label>
      </div>
    </div>

    <div v-if="isCameraActive" class="camera-container has-text-centered">
      <div class="video-wrapper">
        <video
          ref="videoElement"
          autoplay
          playsinline
          muted
          style="
            max-width: 100%;
            max-height: 400px;
            min-height: 300px;
            background: #000;
          "
        ></video>

        <!-- Guía visual de la cara -->
        <div class="face-guide-overlay">
          <div class="face-guide">
            <!-- Contorno principal de la cara -->
            <div class="face-outline"></div>
            <!-- Ojos -->
            <div class="eye left-eye"></div>
            <div class="eye right-eye"></div>
            <!-- Nariz -->
            <div class="nose"></div>
            <!-- Boca -->
            <div class="mouth"></div>
          </div>
          <!-- Texto de ayuda -->
          <div class="guide-text">
            <p>{{ $t("align your face") }}</p>
          </div>
        </div>
      </div>

      <div class="buttons is-centered mt-3">
        <button type="button" class="button is-success" @click="takeSelfie">
          <span class="icon"><i class="fa fa-camera"></i></span>
          <span>{{ $t("capture") }}</span>
        </button>
        <button type="button" class="button" @click="stopCamera">
          <span class="icon"><i class="fa fa-times"></i></span>
          <span>{{ $t("cancel") }}</span>
        </button>
      </div>
    </div>

    <div v-if="selfiePreview" class="has-text-centered">
      <img
        :src="selfiePreview"
        :alt="$t('selfie preview')"
        style="max-width: 300px; max-height: 300px"
      />
      <div class="buttons is-centered mt-3">
        <button type="button" class="button is-primary" @click="retakeSelfie">
          <span class="icon"><i class="fa fa-refresh"></i></span>
          <span>{{ $t("take another") }}</span>
        </button>
      </div>
    </div>

    <canvas ref="canvasElement" style="display: none"></canvas>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";

export default {
  name: "SelfieCapture",
  props: {
    selfiePreview: {
      type: String,
      default: null,
    },
  },
  emits: ["handle-file-upload", "selfie-captured"],
  setup(props, { emit }) {
    const cameraStream = ref(null);
    const isCameraActive = ref(false);
    const videoElement = ref(null);
    const canvasElement = ref(null);

    const activateCamera = async () => {
      try {
        console.log("Solicitando acceso a la cámara...");

        cameraStream.value = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
        });

        console.log("Stream obtenido:", cameraStream.value);

        // Activar la cámara primero para mostrar el video element
        isCameraActive.value = true;

        // Esperar a que el DOM se actualice
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (videoElement.value) {
          console.log("Asignando stream al video element");
          videoElement.value.srcObject = cameraStream.value;

          // Asegurar que el video se reproduzca
          videoElement.value.onloadedmetadata = () => {
            console.log("Video metadata cargado");
            videoElement.value.play().catch((e) => {
              console.error("Error al reproducir video:", e);
            });
          };
        } else {
          console.error("Video element no encontrado");
        }
      } catch (error) {
        console.error("Error al acceder a la cámara:", error);
        isCameraActive.value = false;

        if (error.name === "NotAllowedError") {
          alert(
            "Acceso a la cámara denegado. Por favor permite el acceso y vuelve a intentarlo."
          );
        } else if (error.name === "NotFoundError") {
          alert("No se encontró una cámara disponible.");
        } else {
          alert("No se pudo acceder a la cámara: " + error.message);
        }
      }
    };

    const takeSelfie = () => {
      const canvas = canvasElement.value;
      const video = videoElement.value;

      if (!canvas || !video) {
        console.error("Canvas o video no disponible");
        alert("Error: No se puede acceder al canvas o video");
        return;
      }

      if (video.videoWidth === 0 || video.videoHeight === 0) {
        console.error("Video no tiene dimensiones válidas");
        alert("Error: El video no se ha cargado correctamente");
        return;
      }

      console.log("Capturando selfie:", {
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        readyState: video.readyState,
      });

      const context = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);

      canvas.toBlob(
        (blob) => {
          const preview = canvas.toDataURL("image/jpeg", 0.8);
          stopCamera();

          // Crear un archivo simulado para mantener compatibilidad
          const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });

          emit("selfie-captured", { file, preview });
        },
        "image/jpeg",
        0.8
      );
    };

    const stopCamera = () => {
      if (cameraStream.value) {
        cameraStream.value.getTracks().forEach((track) => track.stop());
        cameraStream.value = null;
        isCameraActive.value = false;
      }
    };

    const retakeSelfie = () => {
      emit("selfie-captured", { file: null, preview: null });
    };

    onUnmounted(() => {
      stopCamera();
    });

    return {
      isCameraActive,
      videoElement,
      canvasElement,
      activateCamera,
      takeSelfie,
      stopCamera,
      retakeSelfie,
    };
  },
};
</script>

<style scoped>
.camera-container {
  background: #f5f5f5;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  min-height: 350px;
}

.video-wrapper {
  position: relative;
  display: inline-block;
}

video {
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.buttons {
  margin-top: 1rem;
}

/* Guía visual de la cara */
.face-guide-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.face-guide {
  position: relative;
  width: 200px;
  height: 250px;
}

/* Contorno principal de la cara (óvalo) */
.face-outline {
  position: absolute;
  width: 180px;
  height: 220px;
  border: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
  top: 10px;
  left: 10px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.5),
    inset 0 0 10px rgba(255, 255, 255, 0.2);
  animation: pulse 2s ease-in-out infinite;
}

/* Ojos */
.eye {
  position: absolute;
  width: 20px;
  height: 15px;
  border: 2px solid rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  top: 80px;
}

.left-eye {
  left: 50px;
}

.right-eye {
  right: 50px;
}

/* Nariz */
.nose {
  position: absolute;
  width: 8px;
  height: 25px;
  border-left: 2px solid rgba(255, 255, 255, 0.7);
  border-radius: 0 0 0 50%;
  top: 120px;
  left: 50%;
  transform: translateX(-50%);
}

/* Boca */
.mouth {
  position: absolute;
  width: 40px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.7);
  border-top: none;
  border-radius: 0 0 40px 40px;
  top: 160px;
  left: 50%;
  transform: translateX(-50%);
}

/* Texto de ayuda */
.guide-text {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 15px;
  border-radius: 15px;
  font-size: 0.9rem;
  white-space: nowrap;
}

/* Animación de pulso */
@keyframes pulse {
  0% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.02);
  }
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
}

/* Responsivo */
@media screen and (max-width: 768px) {
  .face-guide {
    width: 150px;
    height: 200px;
  }

  .face-outline {
    width: 130px;
    height: 170px;
  }

  .eye {
    width: 15px;
    height: 12px;
    top: 60px;
  }

  .left-eye {
    left: 35px;
  }

  .right-eye {
    right: 35px;
  }

  .nose {
    height: 20px;
    top: 95px;
  }

  .mouth {
    width: 30px;
    height: 15px;
    top: 125px;
  }

  .guide-text {
    font-size: 0.8rem;
    bottom: -30px;
  }
}
</style>
